import { Component, Fragment, h, Host, Prop } from '@stencil/core';
import { appRouter } from '../../../global';

@Component({
  tag: 'entry-expert-assignment',
  styleUrl: 'component.css',
  assetsDirs: ['../assets'],
  shadow: true,
})
export class EntryExpertAssignmentPage {
  @Prop({ reflect: true }) summary: boolean = false;

  render() {
    return <Host class="article"
      {...(this.summary && appRouter.click.expertAssignment())}
    >
      <div class="heading content box">
        <h2>Expert Assignment</h2>
        <time>29 Dec 22</time>
      </div>
      <section>
        <div class="body content">
          <blockquote class="hero-background">
            <p>
              Building a web app for delivering assignments to Domain Experts
            </p>
          </blockquote>
        </div>
      </section>

      {this.summary
        ? <div class="body content box">
          <noscript class="right"><a {...appRouter.href.expertAssignment()}>read more &hellip;</a></noscript>
        </div>
        : this.renderMore()}

    </Host>;
  }

  renderMore() {
    return <Fragment>
      <section>
        <h3 class="sub-heading content">
          Actors
        </h3>
        <dl class="body content">
          <dt>The Business</dt>
          <dd>
            <p>Makes critical business descisions based on data collected from many sources including Domain Expert knowledge</p>
            <p>The Domain Information gathered from Experts gives it a competive edge and therefore must be securely guarded</p>
          </dd>
          <dt>The Manager</dt>
          <dd>
            <p>A small team responsible for agreeing and delivering assignments and providing support to the Experts</p>
          </dd>
          <dt>The Domain Expert</dt>
          <dd>
            <p>Responsible for proposing and completing assignments</p>
            <p>Based in locations all over the world</p>
          </dd>
          <dt>Domain Information</dt>
          <dd>
            <p>Information required for the Business to make critical descisions</p>
            <p>The domain is always in flux, updating existing information adding new information</p>
            <p>The Experts use this information and their own Expertise to complete assignments, which are then added to the Domain Information</p>
          </dd>
        </dl>
      </section>

      <section>
        <h3 class="sub-heading content">
          Problem
        </h3>
        <div class="body content">
          <p>
            <em>The Manager</em> has hundreds of Experts to manage
          </p>
          <p>
            They communicate via email to agree the assignments on a bi-weekly basis
          </p>
          <p>They must ensure</p>
          <ol>
            <li>Delivery of assignments is completed for the beginning of the period</li>
            <li>The assignments as a whole, given to the Experts, cover the requirements of the business</li>
            <li>Any changes to assignments are clearly delivered</li>
            <li>The Experts are not over or under worked</li>
            <li>Completed assignments are received by the end of the period</li>
            <li>They manage queries, changes, late or incomplete assignments</li>
            <li>They manage availability of the Experts</li>
          </ol>

          <p>
            <em>The Domain Expert</em> is based in locations all over the world, they work at different times
          </p>
          <p>They must ensure</p>
          <ol>
            <li>Assignments are relevent, appropriate and achievable</li>
            <li>They have access to Domain Information required</li>
            <li>They keep a track of changes made by either party</li>
            <li>Assignments are completed within the given two weeks</li>
            <li>They communicate issues to The Manager</li>
            <li>They communicate availability to The Manager</li>
          </ol>

          <p>If assignments are delivered late, or contain errors or other issues, the Experts time is not used efficiently and the Manager will have to field support requests</p>
          <p>The Business requires more Domain Experts to gather reliable, comparable and broad cover statistics for analysis, in order to make business decisions accurately</p>
          <p>As more Domain Experts are brought into the business, The Manager's role becomes time pressured</p>
          <p>Delivery of the assignments needs to be more secure than current methods e.g. email</p>
          <p>Assignments and Domain Information must be available offline or where network coverage is patchy</p>
        </div>
      </section>

      <section>
        <h3 class="sub-heading content">
          Goals
        </h3>
        <div class="body content">
          <p>In order to relieve time pressure on the Manager and allow the hiring of more Domain Experts the following goals were identified</p>
          <ol>
            <li>Provide an asyncronous, auditable process for agreeing/altering assignments</li>
            <li>Deliver assignments securely on time</li>
            <li>Provide up-to-date, offline available, Domain Information to the Expert so they can complete their assignments to the highest standard</li>
            <li>Ensure assignments and Domain Information are available offline and in patchy network areas</li>
            <li>Reduce chance of misunderstanding or human error requiring more Manager time to resolve</li>
            <li>Manage the Domain Experts availability</li>
          </ol>
        </div>
      </section>

      <section>
        <h3 class="sub-heading page content">
          Proposal
        </h3>
        <div class="body content">
          <p>Create a web application to securely deliver the assignments to the Experts</p>
          <p>The application must allow access to any Domain Information required for the assignment</p>
          <p>It must also provide Domain Information to allow an Expert to propose assignments to the Manager</p>
          <p>This will require additional functionality in the Managers system used to create assignments</p>
          <p>Provide a way for Domain Experts to provide their availability to the Manager</p>
        </div>
      </section>

      <section>
        <h3 class="sub-heading content">
          Architecture
        </h3>
        <div class="body content">
          <p>Building on the existing event sourced system, the new application needs to subscribe to the event stream, and will use existing apis for authentication and common Domain Information</p>
          <p>The app will be written in Typescript for the UI and DonNetCore for the API</p>
          <p>Use the existing <em>Starter App Script</em> which provides a templated application including the required components as NPM or Nuget packages</p>
          <p>The app will be a PWA (Progressive Web Application) to allow for offline support on mobile and desktop</p>
          <p>It will have its own database and api optimised for the Expert app</p>
        </div>
      </section>

      <section>
        <h3 class="sub-heading page content">
          Diagram
        </h3>
        <div class="body content">
          <p><em>shows how the new application fits in to the existing architecture</em></p>
          <div class="diagram">

            <svg xmlns="http://www.w3.org/2000/svg"
              version="1.1"
              viewBox="10 -5 510 590">
              <g>
                <path class="existing" d="M 71 225 C 71 216.72 84.43 210 101 210 C 108.96 210 116.59 211.58 122.21 214.39 C 127.84 217.21 131 221.02 131 225 L 131 275 C 131 283.28 117.57 290 101 290 C 84.43 290 71 283.28 71 275 Z"
                  fill="#fff2cc"
                  pointer-events="all" />
                <path class="existing" d="M 131 225 C 131 233.28 117.57 240 101 240 C 84.43 240 71 233.28 71 225"
                  fill="none"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing" x="101"
                    y="256"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Manager</text>
                  <text class="existing" x="101"
                    y="276"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">DB</text>
                </g>
                <path d="M 391 225 C 391 216.72 404.43 210 421 210 C 428.96 210 436.59 211.58 442.21 214.39 C 447.84 217.21 451 221.02 451 225 L 451 275 C 451 283.28 437.57 290 421 290 C 404.43 290 391 283.28 391 275 Z"
                  fill="#fff2cc"
                  stroke="#d6b656"
                  stroke-width="2"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <path d="M 451 225 C 451 233.28 437.57 240 421 240 C 404.43 240 391 233.28 391 225"
                  fill="none"
                  stroke="#d6b656"
                  stroke-width="2"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text x="421"
                    y="256"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Expert</text>
                  <text x="421"
                    y="276"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">DB</text>
                </g>
                <rect class="existing" x="41"
                  y="520"
                  width="120"
                  height="60"
                  fill="#dae8fc"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing" x="101"
                    y="554"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Manager UI</text>
                </g>
                <rect x="361"
                  y="520"
                  width="120"
                  height="60"
                  fill="#dae8fc"
                  stroke="#6c8ebf"
                  stroke-width="2"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text x="421"
                    y="554"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Expert UI</text>
                </g>
                <path class="existing" d="M 161 390 L 192.63 390"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path class="existing" d="M 197.88 390 L 190.88 393.5 L 192.63 390 L 190.88 386.5 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <path class="existing" d="M 101 360 L 101 300.1"
                  fill="none"
                  stroke="#ccc"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path class="existing" d="M 101 293.35 L 105.5 302.35 L 101 300.1 L 96.5 302.35 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing label out" x="101"
                    y="343"
                    font-family="Helvetica"
                    text-anchor="middle">Query</text>

                </g>
                <path d="M 421 360 L 421 300.1"
                  fill="none"
                  stroke="#ccc"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path d="M 421 293.35 L 425.5 302.35 L 421 300.1 L 416.5 302.35 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="label out" x="421"
                    y="343"
                    font-family="Helvetica"
                    text-anchor="middle">Query</text>

                </g>
                <path d="M 361 390 L 325.37 390"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path d="M 320.12 390 L 327.12 386.5 L 325.37 390 L 327.12 393.5 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <path class="existing" d="M 101 513.63 L 101 426.37"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path class="existing" d="M 101 518.88 L 97.5 511.88 L 101 513.63 L 104.5 511.88 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <path class="existing" d="M 101 421.12 L 104.5 428.12 L 101 426.37 L 97.5 428.12 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing label out" x="101"
                    y="475"
                    font-family="Helvetica"
                    text-anchor="middle">REST &amp; WebSocket</text>

                </g>
                <path d="M 421 513.63 L 421 426.37"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path d="M 421 518.88 L 417.5 511.88 L 421 513.63 L 424.5 511.88 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <path d="M 421 421.12 L 424.5 428.12 L 421 426.37 L 417.5 428.12 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="label out" x="419"
                    y="475"
                    font-family="Helvetica"
                    text-anchor="middle">REST &amp; WebSocket</text>

                </g>
                <path class="existing" d="M 161 360 L 214.08 321.44"
                  fill="none"
                  stroke="#ccc"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path class="existing" d="M 219.54 317.47 L 214.9 326.4 L 214.08 321.44 L 209.61 319.12 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing label out" x="192"
                    y="341"
                    font-family="Helvetica"
                    text-anchor="middle">Query</text>

                </g>
                <path d="M 303.88 321.5 L 356 360"
                  fill="none"
                  stroke="#ccc"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path d="M 298.45 317.49 L 308.36 319.22 L 303.88 321.5 L 303.01 326.46 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-width="3"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="label out" x="326"
                    y="341"
                    font-family="Helvetica"
                    text-anchor="middle">Query</text>

                </g>
                <path class="existing" d="M 135.88 361.28 L 126.46 360 L 205.25 136.69 L 195.35 133.17 L 216.33 120.47 L 224.56 143.57 L 214.67 140.05 Z"
                  fill="#f8cecc"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing label out" x="185"
                    y="212"
                    font-family="Helvetica"
                    text-anchor="middle">Command</text>

                </g>
                <path d="M 395.51 360 L 386.14 361.28 L 303.63 140.02 L 293.79 143.69 L 301.67 120.47 L 322.84 132.86 L 313 136.52 Z"
                  fill="#f8cecc"
                  stroke="#b85450" stroke-width="2"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="label out"
                    x="339"
                    y="221"
                    font-family="Helvetica"
                    text-anchor="middle">Command</text>

                </g>
                <rect class="existing" x="61"
                  y="140"
                  width="80"
                  height="30"
                  fill="#e1d5e7"
                  pointer-events="all" />
                <path d="M 69 140 L 69 170 M 133 140 L 133 170"
                  fill="none"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing" x="101"
                    y="159"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Subs</text>

                </g>
                <path class="existing" d="M 106.7 137.15 L 198.32 91.34"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path class="existing" d="M 102 139.5 L 106.7 133.24 L 106.7 137.15 L 109.83 139.5 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <ellipse class="existing" cx="201"
                  cy="90"
                  rx="3"
                  ry="3"
                  fill="none"
                  stroke="#ccc"
                  pointer-events="all" />
                <path class="existing" d="M 101 170 L 101 203.63"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path class="existing" d="M 101 208.88 L 97.5 201.88 L 101 203.63 L 104.5 201.88 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <rect x="381"
                  y="140"
                  width="80"
                  height="30"
                  fill="#e1d5e7"
                  stroke="#9673a6"
                  stroke-width="2"
                  pointer-events="all" />
                <path d="M 389 140 L 389 170 M 453 140 L 453 170"
                  fill="none"
                  stroke="#9673a6"
                  stroke-width="2"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text x="421"
                    y="159"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Subs</text>

                </g>
                <path d="M 415.36 137.03 L 328.65 91.4"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path d="M 420.01 139.48 L 412.19 139.32 L 415.36 137.03 L 415.45 133.12 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <ellipse cx="326"
                  cy="90"
                  rx="3"
                  ry="3"
                  fill="none"
                  stroke="#ccc"
                  pointer-events="all" />
                <path d="M 421 170 L 421 203.63"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path d="M 421 208.88 L 417.5 201.88 L 421 203.63 L 424.5 201.88 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <path class="existing" d="M 206.5 241 C 206.5 226.33 311.5 226.33 311.5 241 L 311.5 309 C 311.5 323.67 206.5 323.67 206.5 309 Z"
                  fill="#fff2cc"
                  pointer-events="all" />
                <path class="existing" d="M 206.5 241 C 206.5 252 311.5 252 311.5 241"
                  fill="none"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing" x="259"
                    y="273"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Common Domain</text>
                  <text class="existing" x="259"
                    y="293"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Information DB</text>
                </g>
                <rect class="existing" x="41"
                  y="360"
                  width="120"
                  height="60"
                  rx="9"
                  ry="9"
                  fill="#f8cecc"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing" x="101"
                    y="394"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Manager Services</text>

                </g>
                <rect x="361"
                  y="360"
                  width="120"
                  height="60"
                  rx="9"
                  ry="9"
                  fill="#f8cecc"
                  stroke="#b85450"
                  stroke-width="2"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text x="421"
                    y="394"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Expert Services</text>

                </g>
                <rect class="existing" x="199"
                  y="360"
                  width="120"
                  height="60"
                  rx="9"
                  ry="9"
                  fill="#d5e8d4"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing" x="259"
                    y="394"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Auth</text>

                </g>
                <rect class="existing" x="219"
                  y="160"
                  width="80"
                  height="30"
                  fill="#e1d5e7"
                  pointer-events="all" />
                <path class="existing" d="M 227 160 L 227 190 M 291 160 L 291 190"
                  fill="none"
                  pointer-events="all" />
                <g transform="translate(-0.5 -0.5)">
                  <text class="existing" x="259"
                    y="179"
                    fill="#000"
                    font-family="Helvetica"
                    text-anchor="middle">Subs</text>
                </g>
                <path class="existing" d="M 259 120 L 259 153.63"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path class="existing" d="M 259 158.88 L 255.5 151.88 L 259 153.63 L 262.5 151.88 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
                <path class="existing" d="M 259 190 L 259 223.63"
                  fill="none"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="stroke" />
                <path class="existing" d="M 259 228.88 L 255.5 221.88 L 259 223.63 L 262.5 221.88 Z"
                  fill="#ccc"
                  stroke="#ccc"
                  stroke-miterlimit="10"
                  pointer-events="all" />
              </g>
              <path class="existing" d="M 194 0 L 344 0 Q 304 60 344 120 L 194 120 Q 154 60 194 0 Z"
                fill="#fff2cc"
                pointer-events="all" />
              <g transform="translate(-0.5 -0.5)">
                <text class="existing" x="259"
                  y="54"
                  fill="#000"
                  font-family="Helvetica"
                  text-anchor="middle">Domain</text>
                <text class="existing" x="259"
                  y="74"
                  fill="#000"
                  font-family="Helvetica"
                  text-anchor="middle">&amp; Event Store</text>
              </g>
            </svg>
          </div>
        </div>
      </section>

      <section>
        <h3 class="sub-heading page content">
          Tasks
        </h3>
        <div class="body content">
          <p>All tasks were identified and points added using planning poker</p>
          <p><em>note:</em> Domain Expert availability was removed from scope before estimation as it was suggested existing calendars could handle that task for the time being</p>

          <table>
            <thead>
              <tr>
                <th>Description</th>
                <th class="number">Points</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  Project standup using starter app, build actions in GitHub and resource provision in Azure
                </td>
                <td class="number">5</td>
              </tr>
              <tr>
                <td>
                  Domain events for assignments: create, update, change state etc
                </td>
                <td class="number">21</td>
              </tr>
              <tr>
                <td>
                  Expert app event subscriber, API and database
                </td>
                <td class="number">13</td>
              </tr>
              <tr>
                <td>
                  Display assignments in Expert app
                </td>
                <td class="number">8</td>
              </tr>
              <tr class="version-2">
                <td>
                  Propose assignments in Expert app
                </td>
                <td class="number">13</td>
              </tr>
              <tr class="version-2">
                <td>
                  Assignment agree/decline Manager app
                </td>
                <td class="number">13</td>
              </tr>
            </tbody>
            <tfoot>
              <tr class="version-2">
                <th>Total</th>
                <td class="number">73</td>
              </tr>
              <tr class="version-2">
                <th>With Test/UAT at 25%</th>
                <th class="number">91</th>
              </tr>
              <tr>
                <th>Version 1 Total</th>
                <td class="number">47</td>
              </tr>
              <tr>
                <th>With Test/UAT at 25%</th>
                <th class="number">59</th>
              </tr>
            </tfoot>
          </table>
          <p><em>note:</em> This is for illustration, in reality tasks would be broken down futher than this</p>

          <p>This allowed an estimate for completion based on the velocity of the developers given the tasks</p>
          <p><strong>91 points for a single developer at 3 points per day this would be ~30 days</strong></p>
          <p><em>note:</em> for multiple developers we would need to work out which tasks could be done in parallel, which may mean a faster delivery but adds risk</p>

          <p>The crossed out tasks were moved to a second release in order to get the app infront of the Experts as soon as possible</p>
          <p><strong>59 points for a single developer at 3 point per day would estimated ~20 days</strong></p>
          <p>This allowed for a shorter wait for the main benefit of secure distribution of assignments</p>
          <p>It was estimated that delivery could be acheived for a version 1 within a month</p>
        </div>
      </section>

      <section>
        <h3 class="sub-heading page content">
          Outcome
        </h3>
        <div class="body content">
          <p>A single developer was assigned, and, after a round of UAT, the app was delivered on time and well received by users</p>
          <p>The app was covered by unit tests and automated testing provided by a test engineer</p>
          <p>Version 2 was delivered with additional requirements sometime after</p>
          <p>Domain Expert availability remains outside of the application but will be addressed in the future as benefits have been identified</p>

          <p><strong>The success criteria was largely based on the capacity of the Manager to hire more Experts, the Expert team grew by 25% that year</strong></p>
        </div>
      </section>

      <section>
        <h3 class="sub-heading content">
          Learned
        </h3>
        <div class="body content">
          <p>Although all other apps in the system, e.g. Domain Information App, Manager App, are <abbr title="Progressive Web Application">PWA</abbr>'s, they were mainly used as desktop web applications and not installed</p>
          <p>The Expert app was widely installed and used as a <abbr title="Progressive Web Application">PWA</abbr> mobile/desktop app for offline support</p>
          <p>The caching was good for offline support, but bad for updates</p>
          <p>There was a lot of support calls on the second release of the app, with users unable to update or partial updates causing the app to crash</p>
          <p>The caching strategy was changed to load the latest version when a network was available, subsequent updates went well</p>
          <p>Added manual testing of the release, over and above the automated tests to help identify caching issues in future</p>
        </div>
      </section>

      <div class="body content">
        <img class="hero" src="/assets/spy-master.webp" width="1024" height="700" title="AI Image. A Spy Master handing out assignments." />
      </div>

    </Fragment>;
  }
}
